<template>
  <div>
    <retractable-screen-coordinates ref="retractableScreenRef">
      <div class="calc-route__container">
        <Accordion v-model:active-index="activeIndex">
          <!-- activeIndex = 0 -->
          <AccordionTab header="Selección de ruta">
            <div class="grid">
              <div class="town-selector col-12 md:col-5 py-0">
                <Card class="route-card mb-2">
                  <template #title>
                    <h5>
                      Puntos de paso disponibles
                      <span v-tooltip.right="'Buscar y añadir los puntos de paso deseados'">
                        <i class="pi pi-question-circle ml-1" />
                      </span>
                    </h5>
                  </template>
                  <template #content>
                    <div class="search-mode-class">
                      <p>
                        Buscar sólo poblaciones
                      </p>
                      <InputSwitch
                        v-model="globalSearch"
                        class="input-switch"
                        @change="changeGlobalSearch"
                      />
                    </div>
                    <TownSelection
                      v-if="globalSearch"
                      multiple
                      @add-town="addRoutePoint"
                    />
                    <RouteStopSearcher
                      v-if="!globalSearch"
                      multiple
                      @add-route-stop="addRoutePoint"
                    />
                  </template>
                </Card>
              </div>
              <div class="col-12 md:col-7 py-0">
                <Card class="route-card">
                  <template #title>
                    <h5>
                      Puntos de paso seleccionados
                      <span
                        v-tooltip.right="
                          'Seleccionar y arrastrar para cambiar el orden de los puntos de paso'
                        "
                      >
                        <i class="pi pi-question-circle ml-1" />
                      </span>
                    </h5>
                  </template>
                  <template #content>
                    <div class="route-section__container">
                      <RoutePoints
                        :points="routePoints"
                        @calculate-shortest-route="addRoute(true)"
                        @calculate-fastest-route="addRoute(false)"
                        @remove-route-point="removeRoutePoint"
                        @remove-all-points="removeAllPoints"
                      />
                    </div>
                  </template>
                </Card>
              </div>
            </div>
          </AccordionTab>
          <!-- activeIndex = 1 -->
          <AccordionTab
            header="Detalles de la ruta"
            :disabled="!routeLayers || !routeLayers?.length"
          >
            <div class="grid">
              <div class="col-12 md:col-5 md:flex-order-1 py-0">
                <Card class="py-0">
                  <template #title>
                    <h5>Rutas calculadas</h5>
                  </template>
                  <template #content>
                    <div class="route-section__container">
                      <CalculatedRoutes
                        :route-layers="routeLayers"
                        class="route-section__route-list"
                        :selected-route-id="selectedRouteId"
                        @select-route-details="selectRouteDetails"
                        @focus-route="focusRoute"
                        @remove-route="removeRoute"
                      />
                    </div>
                  </template>
                </Card>
              </div>
              <div class="col-12 md:col-7 md:flex-order-0 py-0 route-section">
                <Card class="mb-2">
                  <template #content>
                    <div class="route-section__container">
                      <RouteDetails
                        class="route-section__route-details"
                        :route-point-start="routePointStart"
                        :route-point-end="routePointEnd"
                        :route-info="routeInfo"
                        @go-coordinates="goCoordinates"
                      />
                    </div>
                  </template>
                </Card>
              </div>
            </div>
          </AccordionTab>
        </Accordion>
      </div>
    </retractable-screen-coordinates>
  </div>
</template>

<script setup>
import {
  ref,
  computed,
  onMounted,
  watch,
} from 'vue';
import {useStore} from 'vuex';
import {useToast} from '@sttedra_ui/framework';
import RetractableScreenCoordinates
  from '@/components/retractable-screen-coordinates/RetractableScreenCoordinates';
import Accordion from 'primevue/accordion';
import AccordionTab from 'primevue/accordiontab';
import TownSelection from '@/components/town-selection/TownSelection';
import RouteStopSearcher from '@/components/route-stop/RouteStopSearcher';
import RoutePoints from './RoutePoints';
import RouteDetails from './RouteDetails';
import CalculatedRoutes from '@/components/calculated-routes/CalculatedRoutes';
import Card from 'primevue/card';
import InputSwitch from 'primevue/inputswitch';
import useHandleMOCError from '@/composables/use-handle-moc-error';
import {successToastOptions, warningToastOptions} from '@/util-toast';

// import Timeline from 'primevue/timeline';

// #region Props, Emits

// #endregion

// #region Params, Ref, Reactives, Computed
const sttedraToastService = useToast();
const {handleMOCError} = useHandleMOCError();
const retractableScreenRef = ref();
const activeIndex = ref(0);
const store = useStore();
const routePoints = computed(() => store.state.calcRoute.routePoints);
const selectedRouteId = ref(null);
const routeLayers = computed(() => store.state.userLayers.routeLayers);
const selectedLayer = computed(() => {
  if (!!selectedRouteId.value && !!routeLayers.value) {
    const selId = selectedRouteId.value;
    const selLayer = store.getters['userLayers/getRouteLayerById'](selId);
    return !!selLayer ? selLayer : null;
  }
  return null;
});
const selectedPoints = computed(() => selectedLayer?.value?.points ?? null);
const routeInfo = computed(() => {
  return selectedLayer?.value?.routeInfo ?? [];
});
const routePointStart = computed(() => {
  const points = selectedPoints?.value ?? {};
  if (!!points && !!points.length) {
    return points[0];
  }
  return {};
});
const routePointEnd = computed(() => {
  const points = selectedPoints?.value ?? {};
  if (!!points && !!points.length) {
    return points[points.length - 1];
  }
  return {};
});
const globalSearch = ref(false);
// #endregion

// #region Functions
async function removeRoute(routeId) {
  await store.dispatch('userLayers/removeRouteLayer', {id: routeId}, {root: true});
  sttedraToastService.add(successToastOptions('Ruta eliminada', 'Ruta eliminada correctamente'));
}
async function focusRoute(route) {
  const selLayer = store.getters['userLayers/getRouteLayerById'](route.id);
  if (!!selLayer?.extent?.length) {
    const extent = selLayer.extent;
    await store.dispatch('map/setMapExtent', {extent}, {root: true});
    const handleMinifyFunc = retractableScreenRef.value?.handleMinify ?? null;
    if (!!handleMinifyFunc) {
      await handleMinifyFunc(true);
    }
  }
}
async function selectRouteDetails(route) {
  selectedRouteId.value = route.id;
}
async function addRoute(shortest) {
  const params = {points: routePoints.value.map((p) => {
    return {
      id: p.id,
      name: p.name,
    };
  }), shortestRoute: shortest};
  try {
    await store.dispatch('userLayers/addRouteLayer', params, {root: true});
    sttedraToastService.add(successToastOptions('Ruta añadida', 'Ruta añadida correctamente'));
  } catch (err) {
    const toastOptions = warningToastOptions('Error al añadir ruta');
    handleMOCError(err, toastOptions);
  }
}
async function removeRoutePoint(id) {
  await store.dispatch('calcRoute/removeRoutePoint', {id});
}
async function removeAllPoints() {
  await store.dispatch('calcRoute/cleanRoutePoints');
}
async function changeGlobalSearch() {
  await store.dispatch('calcRoute/addGlobalSearch', globalSearch.value);
}
async function addRoutePoint(point) {
  await store.dispatch('calcRoute/addRoutePoint', {point});
}
async function goCoordinates(infoCoordinates) {
  const goCoordinatesFunc = retractableScreenRef.value?.goCoordinates ?? null;
  if (!!goCoordinatesFunc) {
    await goCoordinatesFunc(infoCoordinates);
  }
}
// #endregion

// #region Effects, Hooks
watch(routeLayers, (newRouteLayers) => {
  if (!!newRouteLayers?.length) {
    const index = newRouteLayers.length - 1;
    selectedRouteId.value = newRouteLayers[index]?.id ?? null;
  }
  activeIndex.value = 1;
}, {deep: true});
watch(selectedLayer, (newSelectedLayer) => {
  if (!newSelectedLayer) {
    activeIndex.value = 0;
    selectedRouteId.value = null;
    if (!!routeLayers?.value?.length) {
      const index = routeLayers.value.length - 1;
      selectedRouteId.value = routeLayers.value[index]?.id ?? null;
      activeIndex.value = 1;
    }
  }
});
onMounted(() => {
  let openTab = 0;
  if (!!routeLayers?.value?.length) {
    if (!selectedLayer?.value) {
      selectedRouteId.value = routeLayers?.value[0].id;
    }
    openTab = 1;
  }
  activeIndex.value = openTab;
});
// #endregion

// #region Provide, Expose

// #endregion
</script>

<style lang="scss" scoped>
@import "src/styles";

.calc-route__container {
  @include route-margin;
  max-height: 66dvh;
  & :deep(a.p-accordion-header-link) {
    border: none;
    text-align: left;
    display: block;
  }
  & :deep(a.p-accordion-toggle-icon) {
    display: hidden;
  }
  & :deep(div.p-accordion-header) {
    border: none;
    width: 100%;
    margin: 0 auto;
  }
  & :deep(div.p-accordion-content) {
    border: none;
  }
  & .route_section .p-card-content {
    padding: 0;
  }
  & .route-section__container {
    max-height: 45dvh;
    overflow-y: auto;
    overflow-x: hidden;
    & .route-section__route-list {
      width: 100%;
    }
    & .route-section__route-details {
      width: 100%;
    }
  }
}
.town-selector .p-datatable-wrapper {
  margin: 8px 0;
}
.route-card {
  min-height: 22dvh;
}
.search-mode-class {
  justify-content: end;
  padding-bottom: 5px;
  display: flex;
}
.input-switch {
  margin-left: 7px;
  margin-top: 8px;
}
</style>
